import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@mui/material/styles';
import { ChevronDown, Check } from 'lucide-react';

export type SearchableMultiSelectProps = {
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  /** Optional display label for empty option (e.g. "—") */
  emptyOptionLabel?: string;
};

export function SearchableMultiSelect({
  label,
  options,
  selected,
  onChange,
  placeholder,
  emptyOptionLabel = '—',
}: SearchableMultiSelectProps) {
  const theme = useTheme();
  const palette = theme.palette;
  const [searchQuery, setSearchQuery] = useState('');
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 200 });

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 200),
    });
  }, [open]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const filteredOptions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => String(opt).toLowerCase().includes(q));
  }, [options, searchQuery]);

  const toggle = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((x) => x !== value)
      : [...selected, value];
    onChange(next);
  };

  const triggerLabel =
    selected.length === 0 ? `All ${label}` : `${selected.length} selected`;

  const contentStyle: React.CSSProperties = {
    position: 'fixed',
    top: position.top,
    left: position.left,
    width: position.width,
    minWidth: 200,
    maxHeight: 300,
    background: palette.background.paper,
    color: palette.text.primary,
    border: `1px solid ${palette.divider}`,
    borderRadius: 8,
    boxShadow: theme.shadows[4],
    padding: 0,
    zIndex: 1400,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    border: 'none',
    borderBottom: `1px solid ${palette.divider}`,
    borderRadius: '8px 8px 0 0',
    background: palette.background.paper,
    color: palette.text.primary,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const listStyle: React.CSSProperties = {
    maxHeight: 240,
    overflow: 'auto',
    padding: 4,
  };

  const itemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 14,
    background: 'transparent',
    color: palette.text.primary,
    border: 'none',
    width: '100%',
    textAlign: 'left',
  };

  const checkStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    borderRadius: 4,
    border: `2px solid ${palette.primary.main}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  const triggerStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '8px 12px',
    height: 36,
    fontSize: 14,
    background: palette.background.paper,
    color: palette.text.primary,
    border: `1px solid ${palette.divider}`,
    borderRadius: 8,
    cursor: 'pointer',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: palette.text.secondary,
    marginBottom: 6,
    display: 'block',
    textAlign: 'left',
  };

  const dropdownPanel = open && typeof document !== 'undefined' && (
    createPortal(
      <div
        ref={panelRef}
        role="listbox"
        aria-label={label}
        style={contentStyle}
      >
        <input
          ref={inputRef}
          type="text"
          role="searchbox"
          placeholder={placeholder ?? `Search ${label}...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={inputStyle}
          aria-label={`Search ${label}`}
        />
        <div style={listStyle}>
          {options.length === 0 ? (
            <div style={{ padding: 12, fontSize: 14, color: palette.text.secondary }}>
              {emptyOptionLabel}
            </div>
          ) : filteredOptions.length === 0 ? (
            <div style={{ padding: 12, fontSize: 14, color: palette.text.secondary }}>
              No results found.
            </div>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = selected.includes(option);
              return (
                <button
                  type="button"
                  key={option}
                  onClick={() => toggle(option)}
                  style={itemStyle}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span
                    style={{
                      ...checkStyle,
                      background: isSelected ? palette.primary.main : 'transparent',
                      color: isSelected ? palette.primary.contrastText : 'transparent',
                    }}
                  >
                    {isSelected ? <Check size={12} /> : null}
                  </span>
                  <span style={{ flex: 1 }}>{option || emptyOptionLabel}</span>
                </button>
              );
            })
          )}
        </div>
      </div>,
      document.body
    )
  );

  return (
    <div style={{ marginBottom: 4, textAlign: 'left' }}>
      <label style={labelStyle}>{label}</label>
      <button
        ref={triggerRef}
        type="button"
        style={triggerStyle}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {triggerLabel}
        </span>
        <ChevronDown size={16} style={{ opacity: 0.7, flexShrink: 0 }} />
      </button>
      {dropdownPanel}
    </div>
  );
}
